import type {
  PublicSurveyDto,
  SurveyAnswerRequest,
  SurveyAnswerResult,
  SurveySubmitRequest,
} from '../../../types/surveyPublic';

// Phase 6B Phase D — client for the PUBLIC survey surface. Uses plain `fetch`
// with credentials:'omit' (the franchisee is unauthenticated; the token is the
// credential) — never the authed `api` helper.

const base = (token: string) => `/api/public/surveys/${encodeURIComponent(token)}`;

export type PublicSurveyFetch =
  | { kind: 'ok'; dto: PublicSurveyDto }
  | {
      kind: 'error';
      state: 'not_found' | 'cancelled' | 'closed' | 'expired' | 'rate_limited' | 'network';
      reason: string;
      closeDate?: string | null;
    };

export async function fetchPublicSurvey(token: string): Promise<PublicSurveyFetch> {
  try {
    const res = await fetch(base(token), { credentials: 'omit' });
    if (res.status === 429) {
      return { kind: 'error', state: 'rate_limited', reason: 'Too many requests. Please wait a moment and refresh.' };
    }
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (res.ok && (body.state === 'active' || body.state === 'submitted')) {
      return { kind: 'ok', dto: body as unknown as PublicSurveyDto };
    }
    const state = (body.state as PublicSurveyFetch extends { state: infer S } ? S : never) ?? 'not_found';
    return {
      kind: 'error',
      state: (state as 'not_found' | 'cancelled' | 'closed' | 'expired') ?? 'not_found',
      reason: (body.reason as string) ?? 'This survey is unavailable.',
      closeDate: (body.closeDate as string | null) ?? null,
    };
  } catch {
    return { kind: 'error', state: 'network', reason: 'Could not load the survey. Check your connection and try again.' };
  }
}

async function postJson<T>(url: string, payload: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error((body.error as string) ?? 'Something went wrong. Please try again.');
  return body as T;
}

export function postAnswer(token: string, req: SurveyAnswerRequest): Promise<SurveyAnswerResult> {
  return postJson<SurveyAnswerResult>(`${base(token)}/answer`, req);
}

export function postSubmit(token: string, req: SurveySubmitRequest): Promise<{ submitted: true }> {
  return postJson<{ submitted: true }>(`${base(token)}/submit`, req);
}
